/**
 * Client-side homeowners API module — replaces home-owner.actions.ts Server Actions.
 */

import { HOME, HOME_OWNERS, AUTH } from "@/shared/constants/backend-api"
import { authFetchJson } from "@/shared/lib/auth-fetch"
import { ApiResponse } from "@/shared/types/backend-response.type"
import {
  HomeOwner,
  HomeOwnerResponse,
  HomeOwnersPaginatedList,
} from "../types/backend-response.type"
import {
  GetHomeOwnerQueryParamsType,
  GetHomeOwnerQueryParamsWithOutPaginationType,
} from "../schema/home/query-params.schema"
import { UpdateHomeOwnerFormType } from "../schema/home-owner.schema"
import { HomeWithHomeOwnerFormType } from "../schema/home-with-home-owner.schema"
import { createHomeStep1, createHomeStep2, createHomeStep3 } from "./homes.api"
import { Home } from "../types/api-response/home-api-response.type"
import { buildUrl } from "@/shared/lib/utils"
import { isNoEmailPlaceholderValue } from "@/features/volunteers/schemas/volunteers.schema"
import {
  buildUnknownLocationPlaceholder,
  isUnknownLocationValue,
} from "../lib/unknown-location"

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const HOMEOWNER_QUERY_KEYS = {
  all: ["homeowners"] as const,
  list: () => [...HOMEOWNER_QUERY_KEYS.all, "list"] as const,
  detail: (id: number) => [...HOMEOWNER_QUERY_KEYS.all, "detail", id] as const,
  home: (homeId: number) => ["home", "detail", homeId] as const,
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function buildNoEmailPlaceholder(fullName?: string) {
  const safeName = (fullName || "homeowner")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 24) || "homeowner"
  return `no-email+${safeName}-${Date.now()}@reliefhub.local`
}

function normalizeNoEmailForCreate(input: any) {
  const out = { ...input }
  if (isNoEmailPlaceholderValue(out.email)) {
    out.email = buildNoEmailPlaceholder(out.name)
  } else if (typeof out.email === "string") {
    out.email = out.email.trim().toLowerCase()
  }
  return out
}

function normalizeUnknownLocationForCreate(home: any, fullName?: string) {
  const out = { ...home }
  if (isUnknownLocationValue(out.address)) {
    out.address = buildUnknownLocationPlaceholder(fullName)
    out.latitude = null
    out.longitude = null
    out.city = out.city?.trim() ? out.city : ""
    out.state = out.state?.trim() ? out.state : ""
    out.zipCode = out.zipCode?.trim() ? out.zipCode : null
  }
  return out
}

// ---------------------------------------------------------------------------
// CRUD
// ---------------------------------------------------------------------------

export async function createHomeOwner(homeOwner: any): Promise<ApiResponse<HomeOwner>> {
  const transformed = normalizeNoEmailForCreate(homeOwner)
  if (transformed.dateOfBirth instanceof Date) {
    const y = transformed.dateOfBirth.getFullYear()
    const m = String(transformed.dateOfBirth.getMonth() + 1).padStart(2, "0")
    const d = String(transformed.dateOfBirth.getDate()).padStart(2, "0")
    transformed.dateOfBirth = `${y}-${m}-${d}`
  }
  return authFetchJson<HomeOwner>(HOME_OWNERS.base, {
    method: "POST",
    body: JSON.stringify(transformed),
  })
}

export async function getHomeOwners(
  queryParams: GetHomeOwnerQueryParamsType,
): Promise<ApiResponse<HomeOwnersPaginatedList>> {
  const url = buildUrl(HOME_OWNERS.base, queryParams as any)
  return authFetchJson<HomeOwnersPaginatedList>(url)
}

export async function getAllHomeOwners(
  queryParams?: GetHomeOwnerQueryParamsWithOutPaginationType,
): Promise<ApiResponse<HomeOwnersPaginatedList>> {
  const url = buildUrl(HOME_OWNERS.base, {
    ...queryParams,
    limit: 1000,
    page: 1,
  } as any)
  return authFetchJson<HomeOwnersPaginatedList>(url)
}

export async function getHomeOwner(homeOwnerId: number): Promise<ApiResponse<HomeOwner>> {
  return authFetchJson<HomeOwner>(HOME_OWNERS.byId(homeOwnerId))
}

export async function updateHomeOwner(
  homeOwnerId: number,
  homeOwner: UpdateHomeOwnerFormType,
): Promise<ApiResponse<HomeOwnerResponse>> {
  return authFetchJson<HomeOwnerResponse>(HOME_OWNERS.byId(homeOwnerId), {
    method: "PUT",
    body: JSON.stringify(homeOwner),
  })
}

export async function deleteHomeowner(homeOwnerId: number): Promise<ApiResponse> {
  return authFetchJson(HOME_OWNERS.byId(homeOwnerId), { method: "DELETE" })
}

// ---------------------------------------------------------------------------
// Home queries (convenience wrappers — originally in home-owner.actions.ts)
// ---------------------------------------------------------------------------

export async function getHome(homeId: number): Promise<ApiResponse<Home>> {
  return authFetchJson<Home>(HOME.details(homeId))
}

// ---------------------------------------------------------------------------
// Multi-step create
// ---------------------------------------------------------------------------

export async function createHomeWithHomeOwner(
  homeWithHomeOwner: HomeWithHomeOwnerFormType,
): Promise<ApiResponse> {
  const hoRes = await createHomeOwner(homeWithHomeOwner.homeOwner)
  if (!hoRes.success) return hoRes
  const homeOwnerId = hoRes.data.id

  const home = normalizeUnknownLocationForCreate(
    homeWithHomeOwner.home,
    homeWithHomeOwner.homeOwner?.name,
  )
  const h1 = await createHomeStep1(homeOwnerId, home)
  if (!h1.success) return h1
  const h2 = await createHomeStep2(homeOwnerId, home)
  if (!h2.success) return h2
  const h3 = await createHomeStep3(homeOwnerId, home)
  if (!h3.success) return h3

  return {
    success: true,
    data: { ...(h3.data as Record<string, any>), homeownerId: homeOwnerId },
  }
}

// ---------------------------------------------------------------------------
// Send login link
// ---------------------------------------------------------------------------

export async function SendLink(email: string): Promise<ApiResponse<any>> {
  const res = await authFetchJson<any>(AUTH.sendLink, {
    method: "POST",
    body: JSON.stringify({ email }),
    skipAuth: true,
  })
  if (!res.success) return res
  return { success: true as const, data: res.data?.user }
}
